import {Divider, Grid} from '@material-ui/core';
import {FormikHelpers} from 'formik';
import {ReactElement, useCallback, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MerchantContext} from '../../App';
import {MERCHANT_URL, MERCHANTS_URL, SETTLEMENTS_URL} from '../../shared/constants/api-urls';
import useGet, {useGetExt} from '../../shared/hooks/use-get.hook';
import usePut, {usePutExt} from '../../shared/hooks/use-put.hook';
import {EnvironmentType} from '../../shared/model/EnvironmentType.model';
import {HttpError, SimpleErrorBody} from '../../shared/model/Error.model';
import {Merchant, Settlement} from '../../shared/model/Merchant.model';
import PlatformType from '../../shared/model/PlatformType.model';
import {SettlementInstitution} from '../../shared/model/SettlementInstitution.model';
import {Shop, ShopCreateResponse} from '../../shared/model/Shop.model';
import {TransHelper} from '../../utils/trans-helper';
import MainContainer from '../shared/main-container/MainContainer';
import {BannerMessage, BannerType} from '../shared/main-container/MainContainer.model';
import CustomEnvironmentSettings from './environment/custom/CustomEnvironmentSettings';
import {CustomEnvironmentAttributes} from './environment/custom/CustomEnvironmentSettings.model';
import {EnvironmentFormFields, EnvironmentsFormFields} from './environment/Environment.form.model';
import {
  Environment,
  EnvironmentAttributes,
  EnvironmentAttributesType
} from './environment/Environment.model';
import ShopifyEnvironmentSettings from './environment/shopify/ShopifyEnvironmentSettings';
import {ShopifyEnvironmentAttributes} from './environment/shopify/ShopifyEnvironmentSettings.model';
import WoocommerceEnvironmentSettings from './environment/woocommerce/WoocommerceEnvironmentSettings';
import {WoocommerceEnvironmentAttributes} from './environment/woocommerce/WoocommerceEnvironmentSettings.model';
import SettlementSettings from './settlement/SettlementSettings';
import {SettlementFormFields} from './settlement/SettlementSettings.model';
import ShopForm from './shop/ShopSettings';
import {ShopFormFields} from './shop/ShopSettings.model';
import styles from './Settings.module.scss';
import axios, {CancelTokenSource} from 'axios';
import NxInput from '../shared/nxInput/NxInput';

const PrefixTrans = TransHelper.getPrefixedTrans('SETTINGS');
export const SETTINGS_ERROR_KEY = 'settings_error';

export default function Settings(): ReactElement {

  const [environments, setEnvironments] = useState<Array<Environment<EnvironmentAttributes>>>();
  const [bannerData, setBannerData] = useState<BannerMessage>();
  const [institutions, setInstitutions] = useState<Array<SettlementInstitution>>();
  const {t} = useTranslation();
  const {setCurrentMerchant, setMerchants, currentMerchant, currentMerchantId} = useContext(MerchantContext);
  const environmentRequest = useGetExt<Array<Environment<EnvironmentAttributes>>>();
  const institutionsRequest = useGet<Array<SettlementInstitution>>(`${SETTLEMENTS_URL}/institutions`);
  const updateSettlement = usePut<Settlement, Settlement>(`${MERCHANTS_URL}/${currentMerchantId}/settlements`);
  const updateShop = usePut<ShopCreateResponse, Shop>(`${MERCHANTS_URL}/${currentMerchantId}`);
  const merchantsRequest = useGet<Array<Merchant>>(MERCHANTS_URL);
  const updateEnvAttributesRequest = usePutExt<void, EnvironmentAttributes>();

  const getSettingsData = useCallback(async (cancelTokenSource?: CancelTokenSource) => {
    if (currentMerchantId) {
      await environmentRequest(`${MERCHANT_URL}/${currentMerchantId}/environments`, null, cancelTokenSource)
        .then(setEnvironments);
    }
    if (!institutions || !institutions.length) {
      await institutionsRequest(null, cancelTokenSource)
        .then(setInstitutions);
    }
  }, [currentMerchantId]);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    getSettingsData(cancelTokenSource);

    return cancelTokenSource.cancel;
  }, [currentMerchantId]);

  const scrollToTop = (): void => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });

  const isAnyValueChanged = (object1: object, object2: object): boolean => {
    for (const key in object1) {
      if (object1[key] !== object2[key]) {
        return true;
      }
    }
    return false;
  };

  const isAnyAttributeValueChanged = (attributes: EnvironmentAttributes, envId: number): boolean => {
    const oldEnv = environments?.find(env => env.id === envId) ?? {attributes: {}};
    return isAnyValueChanged(oldEnv.attributes, attributes);
  };

  const handleSubmitError = <E extends EnvironmentsFormFields<EnvironmentFormFields> | SettlementFormFields | ShopFormFields>
    (httpError: HttpError<SimpleErrorBody>, actions: FormikHelpers<E>): void => {
    setBannerData({
      type: BannerType.ERROR,
      message: t('SETTINGS.ENVIRONMENT.SUBMIT_ERROR')
    });
    actions.setFieldError(
      SETTINGS_ERROR_KEY,
      httpError.error.errorMessage
    );
    scrollToTop();
  };

  const submitEnvAttributes = async <T extends EnvironmentAttributes, E extends EnvironmentFormFields>
    (envType: EnvironmentType, values: EnvironmentsFormFields<E>): Promise<boolean> => {
    const envValues = values[envType];

    if (!envValues) {
      return Promise.resolve(false);
    }

    const payload: T = {
      ...values[envType],
      type: EnvironmentAttributesType[envValues.type]
    } as T;

    if (isAnyAttributeValueChanged(payload, envValues.environmentId)) {
      return await updateEnvAttributesRequest(
        `${MERCHANT_URL}/${currentMerchantId}/environments/${envValues.environmentId}/attributes`,
        payload
      ).then(() => Promise.resolve(true));
    }

    return Promise.resolve(false);
  };

  const submitEnvironmentAttributes = async <T extends EnvironmentAttributes, E extends EnvironmentFormFields>
    (values: EnvironmentsFormFields<E>, actions: FormikHelpers<EnvironmentsFormFields<E>>): Promise<void> => {
    let anyEnvUpdated = false;

    for (const key of Object.keys(EnvironmentType)) {
      try {
        const envUpdated = await submitEnvAttributes<T, E>(EnvironmentType[key], values);
        if (envUpdated) {
          anyEnvUpdated = true;
        }
      } catch (e) {
        handleSubmitError(e as HttpError<SimpleErrorBody>, actions);
      }
    }

    if (anyEnvUpdated) {
      setBannerData({
        type: BannerType.SUCCESS,
        message: t('SETTINGS.ENVIRONMENT.SUBMIT_SUCCESS')
      });
      scrollToTop();
      await getSettingsData();
    }

    return Promise.resolve();
  };

  const submitSettlements = async (
    values: SettlementFormFields,
    actions: FormikHelpers<SettlementFormFields>): Promise<void> => {
    const payload: SettlementFormFields = {
      ...values
    };

    if (!currentMerchant || !isAnyValueChanged(payload, currentMerchant)) {
      return Promise.resolve();
    }

    try {
      await updateSettlement(payload);
      const merchants = await merchantsRequest();
      setMerchants(merchants);
      setCurrentMerchant(merchants.find(merchant => merchant.id === currentMerchantId));
      setBannerData({
        type: BannerType.SUCCESS,
        message: t('SETTINGS.ENVIRONMENT.SUBMIT_SUCCESS')
      });
      scrollToTop();
    } catch (error) {
      handleSubmitError(error as HttpError<SimpleErrorBody>, actions);
    }
  };

  const submitShopSettings = async (
    values: ShopFormFields,
    actions: FormikHelpers<ShopFormFields>): Promise<void> => {
    const shop: Shop = {
      ...values,
      platform: PlatformType[values.platform]
    };

    if (!currentMerchant || !isAnyValueChanged(values, currentMerchant)) {
      return Promise.resolve();
    }

    try {
      await updateShop(shop);
      const merchants = await merchantsRequest();
      setMerchants(merchants);
      setCurrentMerchant(merchants.find(merchant => merchant.id === currentMerchantId));
      setBannerData({
        type: BannerType.SUCCESS,
        message: t('SETTINGS.ENVIRONMENT.SUBMIT_SUCCESS')
      });
      await getSettingsData();
      scrollToTop();
    } catch (error) {
      handleSubmitError(error as HttpError<SimpleErrorBody>, actions);
    }
  };

  const environmentSettings = useCallback(() => {
    if (!environments || environments.length === 0) {
      return <></>;
    }

    switch (currentMerchant?.platform) {
      case PlatformType.CUSTOM:
        return <>
          <CustomEnvironmentSettings
            environments={environments as Array<Environment<CustomEnvironmentAttributes>>}
            submitEnvironments={submitEnvironmentAttributes} />
          <Divider />
        </>;
      case PlatformType.SHOPIFY:
        return <>
          <ShopifyEnvironmentSettings
            environments={environments as Array<Environment<ShopifyEnvironmentAttributes>>}
            submitEnvironments={submitEnvironmentAttributes} />
          <Divider />
        </>;
      case PlatformType.WOOCOMMERCE:
        return <>
          <WoocommerceEnvironmentSettings
            environments={environments as Array<Environment<WoocommerceEnvironmentAttributes>>}
            submitEnvironments={submitEnvironmentAttributes} />
          <Divider />
        </>;
      default:
        return <></>;
    }
  }, [environments]);

  const settlementsSettings = useCallback((): ReactElement => (
    currentMerchant
      ? <>
        <SettlementSettings
          merchant={currentMerchant as Merchant}
          submitSettlements={submitSettlements}
          institutions={institutions ?? []} />
        <Divider />
      </>
      : <></>
  ),
    [currentMerchant, institutions]);

  const shopSettings = useCallback((): ReactElement => (
    currentMerchant
      ? <ShopForm merchant={currentMerchant as Merchant} submit={submitShopSettings} />
      : <></>
  ),
    [currentMerchant]);

  return (
    <MainContainer bannerMessage={bannerData}>
      <Grid container className={styles.title}>
        <div className={styles.title}>
          <PrefixTrans>TITLE</PrefixTrans>
        </div>
      </Grid>
      <NxInput
        className={styles.staticInput}
        label={<PrefixTrans>MERCHANT_ID</PrefixTrans>}
        value={String(currentMerchantId ?? '')}
        disabled />
      {environmentSettings()}
      {settlementsSettings()}
      {shopSettings()}
    </MainContainer>
  );
}
